/**
 * Serviço para buscar histórico unificado via timeline
 * Rota: GET /cdr/historico/timeline
 */

import { api } from "../lib/api";
import type {
	TimelineResponse,
	TimelineFilters,
	TimelineSourceFilter,
} from "../types/historico";

export class HistoricoService {
	/**
	 * Busca histórico unificado via timeline
	 * Rota: GET /cdr/historico/timeline
	 *
	 * @param filters - Filtros opcionais (source, page, limit, date_from, date_to, search)
	 * @returns Promise<TimelineResponse>
	 */
	static async fetchTimeline(
		filters?: TimelineFilters,
	): Promise<TimelineResponse> {
		const params = new URLSearchParams();

		// Source filter (default: all)
		if (filters?.source && filters.source !== "all") {
			params.append("source", filters.source);
		}

		// Paginação
		if (filters?.page) {
			params.append("page", filters.page.toString());
		}
		if (filters?.limit) {
			params.append("limit", filters.limit.toString());
		}

		// Filtros de data
		if (filters?.date_from) {
			params.append("date_from", filters.date_from);
		}
		if (filters?.date_to) {
			params.append("date_to", filters.date_to);
		}

		// Busca textual
		if (filters?.search && filters.search.trim()) {
			params.append("search", filters.search.trim());
		}

		const queryString = params.toString();
		const url = `/cdr/historico/timeline${queryString ? `?${queryString}` : ""}`;

		const response = await api(url);
		return response as TimelineResponse;
	}

	/**
	 * Busca histórico com source específico
	 * Atalho para fetchTimeline com source definido
	 */
	static async fetchBySource(
		source: TimelineSourceFilter,
		page: number = 1,
		limit: number = 20,
		filters?: Omit<TimelineFilters, "source" | "page" | "limit">,
	): Promise<TimelineResponse> {
		return this.fetchTimeline({
			source,
			page,
			limit,
			...filters,
		});
	}

	/**
	 * Busca apenas file_save (salvar na PRE)
	 */
	static async fetchFileSave(
		page: number = 1,
		limit: number = 20,
		filters?: Omit<TimelineFilters, "source" | "page" | "limit">,
	): Promise<TimelineResponse> {
		return this.fetchBySource("file_save", page, limit, filters);
	}

	/**
	 * Busca apenas pre_file_save (exportar pra PRE)
	 */
	static async fetchPreFileSave(
		page: number = 1,
		limit: number = 20,
		filters?: Omit<TimelineFilters, "source" | "page" | "limit">,
	): Promise<TimelineResponse> {
		return this.fetchBySource("pre_file_save", page, limit, filters);
	}

	/**
	 * Busca apenas orcamentos_temp (exportar temporário)
	 */
	static async fetchOrcamentosTemp(
		page: number = 1,
		limit: number = 20,
		filters?: Omit<TimelineFilters, "source" | "page" | "limit">,
	): Promise<TimelineResponse> {
		return this.fetchBySource("orcamentos_temp", page, limit, filters);
	}
}
